import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { getPaymentMethodsQuery } from "rsl-api-106/lib/queries";
import {
  Maybe,
  Option,
  Query,
  QueryGetPaymentMethodsArgs,
} from "rsl-api-106/lib/types";

import { useQuery } from "@apollo/client";
import PaymentLogo from "@components/Payments/PaymentLogo";
import { faCircle } from "@fortawesome/free-regular-svg-icons";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RadioGroup } from "@headlessui/react";
import { useConfig, useSession } from "@hooks";

type SelectPaymentMethodsProps = {
  selected: string | undefined;
  setSelected: (value: string) => void;
  selectedPaymentOption: string | undefined;
  setSelectedPaymentOption: (value: string) => void;
  automaticPaymentOnly?: boolean;
  paymentDueTypeSelected?: string;
  hideANCV?: boolean;
};

const languagesVarsCodes = [
  "LANG_neobooking_paymentmethods", //Mode de paiement
  "LANG_neobooking_paymentmethods_options", //Option de paiement
];

const SelectPaymentMethods: React.FC<SelectPaymentMethodsProps> = ({
  selected,
  setSelected,
  selectedPaymentOption,
  setSelectedPaymentOption,
  automaticPaymentOnly = false,
  paymentDueTypeSelected,
  hideANCV = false,
}) => {
  /* Hooks */
  const { sessionId } = useSession();
  const { data, loading, error, refetch } = useQuery<
    Query,
    QueryGetPaymentMethodsArgs
  >(getPaymentMethodsQuery(), {
    variables: {
      session: { name: sessionId!! },
    },
    fetchPolicy: "no-cache",
  });
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";

  /* State */
  const [paymentMethods, setPaymentMethods] = useState<
    Maybe<Option>[] | undefined
  >([]);
  const [paymentOptions, setPaymentOptions] = useState<
    Maybe<Option>[] | undefined
  >([]);

  useEffect(() => {
    if (data?.getPaymentMethods?.paymentMethod) {
      const automaticPaymentCode =
        data.getPaymentMethods?.paymentMethod?.automaticPaymentCode;
      setPaymentMethods(
        automaticPaymentOnly && automaticPaymentCode
          ? data.getPaymentMethods?.paymentMethod?.paymentMethod?.filter(
              (x) => x?.code === automaticPaymentCode
            )!!
          : data.getPaymentMethods?.paymentMethod?.paymentMethod!!
      );
      setPaymentOptions(
        data.getPaymentMethods?.paymentMethod?.automaticPaymentMethod!!
      );
      !selected &&
        setSelected(
          automaticPaymentOnly && automaticPaymentCode
            ? data.getPaymentMethods?.paymentMethod?.paymentMethod?.filter(
                (x) => x?.code === automaticPaymentCode
              )![0]?.code!!
            : data.getPaymentMethods?.paymentMethod?.paymentMethod![0]?.code!!
        );
      if (
        !selectedPaymentOption &&
        data.getPaymentMethods?.paymentMethod?.automaticPaymentMethod &&
        data.getPaymentMethods?.paymentMethod?.automaticPaymentMethod.length > 0
      ) {
        setSelectedPaymentOption(
          data.getPaymentMethods?.paymentMethod?.automaticPaymentMethod![0]
            ?.code!!
        );
      }
    }
  }, [data]);

  useEffect(() => {
    if (paymentDueTypeSelected && paymentDueTypeSelected !== "0") {
      refetch();
    }
  }, [paymentDueTypeSelected]);

  return (
    <>
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div>
          {loading ? (
            <div className="tw-mt-4">
              <LoadingPaymentMethods items={3} />
            </div>
          ) : (
            paymentMethods?.length!! > 1 && (
              <RadioGroup value={selected} onChange={setSelected}>
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-2">
                  {paymentMethods?.map((method) => {
                    return (
                      <RadioGroup.Option
                        as={"div"}
                        key={method?.code}
                        value={method?.code}
                        className={({ active, checked }) =>
                          `${
                            active
                              ? `tw-border ${
                                  mode === "vacanceole"
                                    ? "tw-border-[#707070]"
                                    : "tw-ring-offset-primary_corseole-500"
                                } tw-ring-white`
                              : ""
                          }
              ${
                checked
                  ? `tw-text-white tw-border ${
                      mode === "vacanceole"
                        ? "tw-bg-v_blue-light tw-border-[#707070]"
                        : "tw-bg-primary_corseole-200 tw-ring-offset-primary_corseole-500"
                    } tw-ring-white`
                  : "tw-bg-white"
              }
              tw-relative tw-border tw-shadow tw-px-5 tw-py-4 tw-cursor-pointer tw-flex focus:tw-outline-none tw-rounded-[26px]`
                        }
                      >
                        {({ active, checked }) => (
                          <>
                            <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                              <div className="tw-flex tw-items-center">
                                <div className="tw-text-sm">
                                  <RadioGroup.Description
                                    as="span"
                                    className={`tw-inline ${
                                      checked
                                        ? `${
                                            mode === "vacanceole"
                                              ? "tw-text-primary-100"
                                              : "tw-text-primary_corseole-100"
                                          }`
                                        : "tw-text-gray-500"
                                    }`}
                                  >
                                    <div
                                      style={{ width: "60px", height: "40px" }}
                                    >
                                      <PaymentLogo option={method!!} />
                                    </div>
                                  </RadioGroup.Description>
                                </div>
                              </div>
                              {checked ? (
                                <div className="tw-flex-shrink-0 tw-text-white">
                                  <FontAwesomeIcon
                                    className={
                                      mode === "vacanceole"
                                        ? "tw-text-v_blue-dark"
                                        : "tw-text-primary_corseole-800"
                                    }
                                    icon={faCheckCircle}
                                  />
                                </div>
                              ) : (
                                <div className="tw-flex-shrink-0 tw-text-white">
                                  <FontAwesomeIcon
                                    className={
                                      mode === "vacanceole"
                                        ? "tw-text-v_blue-dark"
                                        : "tw-text-primary_corseole-800"
                                    }
                                    icon={faCircle}
                                  />
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </RadioGroup.Option>
                    );
                  })}
                </div>
              </RadioGroup>
            )
          )}
        </div>
        {selected ===
          data?.getPaymentMethods?.paymentMethod?.automaticPaymentCode && (
          <div>
            {" "}
            {loading ? (
              <LoadingPaymentMethods items={3} />
            ) : (
              <RadioGroup
                value={selectedPaymentOption}
                onChange={setSelectedPaymentOption}
              >
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-2">
                  {paymentOptions
                    ?.filter((x) => (hideANCV === true ? x?.code !== "28" : x))
                    ?.map((method) => {
                      return (
                        <RadioGroup.Option
                          as={"div"}
                          key={method?.code}
                          value={method?.code}
                          className={({ active, checked }) =>
                            `${
                              active
                                ? `tw-border ${
                                    mode === "vacanceole"
                                      ? "tw-border-[#707070]"
                                      : "tw-ring-offset-primary_corseole-500"
                                  } tw-ring-white`
                                : ""
                            }
              ${
                checked
                  ? `tw-text-white tw-border ${
                      mode === "vacanceole"
                        ? "tw-bg-v_blue-light tw-border-[#707070]"
                        : "tw-bg-primary_corseole-200 tw-ring-offset-primary_corseole-500"
                    } tw-ring-white`
                  : "tw-bg-white"
              }
              tw-relative tw-border tw-shadow tw-px-5 tw-py-4 tw-cursor-pointer tw-flex focus:tw-outline-none tw-rounded-[26px]`
                          }
                        >
                          {({ active, checked }) => (
                            <>
                              <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                                <div className="tw-flex tw-items-center">
                                  <div className="tw-text-sm">
                                    <RadioGroup.Description
                                      as="span"
                                      className={`tw-inline ${
                                        checked
                                          ? `${
                                              mode === "vacanceole"
                                                ? "tw-text-primary-100"
                                                : "tw-text-primary_corseole-100"
                                            }`
                                          : "tw-text-gray-500"
                                      }`}
                                    >
                                      <div
                                        style={{
                                          width: "60px",
                                          height: "40px",
                                        }}
                                      >
                                        <PaymentLogo option={method!!} />
                                      </div>
                                    </RadioGroup.Description>
                                  </div>
                                </div>
                                {checked ? (
                                  <div className="tw-flex-shrink-0 tw-text-white">
                                    <FontAwesomeIcon
                                      className={
                                        mode === "vacanceole"
                                          ? "tw-text-v_blue-dark"
                                          : "tw-text-primary_corseole-800"
                                      }
                                      icon={faCheckCircle}
                                    />
                                  </div>
                                ) : (
                                  <div className="tw-flex-shrink-0 tw-text-white">
                                    <FontAwesomeIcon
                                      className={
                                        mode === "vacanceole"
                                          ? "tw-text-v_blue-dark"
                                          : "tw-text-primary_corseole-800"
                                      }
                                      icon={faCircle}
                                    />
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </RadioGroup.Option>
                      );
                    })}
                </div>
              </RadioGroup>
            )}
          </div>
        )}
      </div>
    </>
  );
};

SelectPaymentMethods.propTypes = {
  //
};

type LoadingPaymentMethodsProps = {
  items: number;
};

const LoadingPaymentMethods: React.FC<LoadingPaymentMethodsProps> = ({
  items,
}) => {
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";

  return (
    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-2">
      {[...Array(items)].map((_, i) => {
        return (
          <div
            key={`paymentmethods_${i}`}
            className="tw-animate-pulse tw-bg-gray-50 tw-relative tw-border tw-shadow tw-px-5 tw-py-4 tw-flex focus:tw-outline-none tw-rounded-[26px]"
          >
            <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
              <div className="tw-flex tw-items-center">
                <div className="tw-text-sm">
                  <span>
                    <div
                      className="tw-bg-gray-200 tw-rounded-md"
                      style={{ width: "60px", height: "40px" }}
                    ></div>
                  </span>
                </div>
              </div>
              <div className="tw-flex-shrink-0 tw-text-white">
                <span
                  className={`${
                    mode === "vacanceole"
                      ? "tw-text-v_blue-dark"
                      : "tw-text-primary_corseole-800"
                  }tw-w-3 tw-h-3 tw-rounded-full`}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SelectPaymentMethods;
